import React from 'react';
import { IataRequirementsList } from '../../data/iataRules';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, ArrowRight, AlertTriangle, CheckCircle } from 'lucide-react';
import { SEOLayout } from '../../components/SEOLayout';
import { allGuidesData as guidesData } from '../../data/seoDataMerged';

const GuidesHub = () => {
  const guides = Object.values(guidesData);

  const GuideCard = ({ guide }) => (
    <Link 
      to={`/guides/${guide.slug}`}
      className="block bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 transition-all group"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
          <BookOpen className="w-6 h-6 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <span className="bg-slate-100 px-2 py-1 rounded capitalize">{guide.category.replace(/-/g, ' ')}</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {guide.readTime} read
            </span>
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
            {guide.title}
          </h3>
          <p className="text-slate-600 text-sm line-clamp-2">
            {guide.introduction}
          </p>
          <div className="mt-4 text-blue-600 text-sm font-medium flex items-center gap-1">
            Read guide <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <SEOLayout
      title="Pet Air Travel Guides 2026 | Expert Tips & Complete Checklists"
      description="Expert guides on flying with pets. Crate requirements, IATA sizing, airline policies, document checklists. Everything you need to know for safe pet travel."
      keywords={['pet travel guide', 'flying with pets tips', 'IATA crate size', 'pet travel checklist', 'airline pet requirements']}
      breadcrumbs={[{ label: 'Guides' }]}
    >
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <BookOpen className="w-4 h-4" />
          Expert Resources
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Pet Travel Guides & Resources
        </h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Everything you need to know about flying with your pet. From IATA crate requirements 
          to airline-specific policies, our comprehensive guides help you avoid rejection and 
          travel stress-free.
        </p>
      </div>

      {/* Featured Guides */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Essential Reading</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {guides.slice(0, 4).map(guide => (
            <GuideCard key={guide.slug} guide={guide} />
          ))}
        </div>
      </section>

      {/* Quick Tips Section */}
      <section className="bg-slate-100 rounded-2xl p-8 mb-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Quick Tips</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold text-green-700 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              DO's - Best Practices
            </h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                <span>Replace plastic clips with metal bolts </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                <span>Measure your pet using IATA formula, not manufacturer sizing</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                <span>Get health certificate within 10 days of travel</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                <span>Attach food/water bowls accessible from outside</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                <span>Add "Live Animal" stickers and "This Way Up" arrows</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                <span>Freeze water bowl so it melts slowly during flight</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              DON'Ts - Common Mistakes
            </h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                <span>Don't use soft-sided carriers for cargo - instant rejection</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                <span>Don't leave wheels attached to rolling crates</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                <span>Don't sedate your pet without vet approval - most airlines prohibit</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                <span>Don't fly snub-nosed breeds in cargo - death risk</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                <span>Don't feed pet 4-6 hours before flight</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-1" />
                <span>Don't assume "airline approved" crates meet cargo requirements</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* More Guides */}
      {guides.length > 4 && (
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">More Guides</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {guides.slice(4).map(guide => (
              <GuideCard key={guide.slug} guide={guide} />
            ))}
          </div>
        </section>
      )}

      {/* IATA Quick Reference */}
      <section className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-6">IATA Crate Size Quick Reference</h2>
        <IataRequirementsList className="bg-white rounded-xl p-4 mb-6" />
        <div className="text-center">
          <Link to="/guides/iata-crate-size-calculator">
            <span className="text-blue-700 font-medium hover:text-blue-800">
              View full IATA sizing guide →
            </span>
          </Link>
        </div>
      </section>
    </SEOLayout>
  );
};

export default GuidesHub;
